import { getScopedItem, STORAGE_KEYS } from "../components/common/Helper";
import APIService, {setAuthToken} from "./APIService";
import DebugService from "./DebugService";
import APIConstants from "./APIConstants";

/**
* @description AI Chatbot Service
* Handles AI chatbot access key management
* and AI chatbot interactions.
* @author Ashish Sharma
*/
const AIChatbotService = {

  /**
  * =========================
  * AI CHATBOT ACCESS KEY APIS
  * =========================
  */
  
  /**
  * @description Fetches all AI chatbot access keys linked
  * to the logged-in user. The access keys are used to
  * authenticate and interact with AI Chatbot services. 
  * @author Ashish Sharma
  */
  getAiChatbotAccessKeys: async () => {
    const url = APIConstants.ENDPOINT_AI_CHATBOT_ACCESS_KEYS;

    if (getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN)) {
      setAuthToken(getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN));
    }

    try {
      const response = await APIService.get(url);
      return response?.data || [];
    } catch (err) {
      DebugService.error("AIChatbotService: Error fetching user access keys", err);
      throw err;
    }
  },

  /**
  * @description Creates a new AI chatbot access key for the logged-in user.
  * @author Ashish Sharma
  */
  addAiChatbotAccessKey: async (payload) => {
    const url = APIConstants.ENDPOINT_AI_CHATBOT_ACCESS_KEYS;

    if (getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN)) {
      setAuthToken(getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN));
    }

    try {
      const response = await APIService.post(url, payload);
      if (response?.error) {
        DebugService.error("AIChatbotService: Error adding AI access key", response.error);
        throw new Error(response.error);
      }
      return response?.data;
    } catch (err) {
      DebugService.error("AIChatbotService: Error adding AI access key", err);
      throw err;
    }
  },

  /**
  * @description Updates an existing AI chatbot access key for the user. 
  * Identifies the key using the unique
  * userAiAccessKeyId and updates the access key value.
  * @author Ashish Sharma
  */
  updateAiChatbotAccessKey: async (payload) => {
    const url = APIConstants.ENDPOINT_AI_CHATBOT_ACCESS_KEYS;

    if (getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN)) {
      setAuthToken(getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN));
    }

    try {
      const response = await APIService.put(url, payload);
      return response?.data;
    } catch (err) {
      DebugService.error("AIChatbotService: Error updating AI access key", err);
      throw err;
    }
  },

  /**
  * @description Deletes an AI chatbot access key associated with the logged-in user. 
  * Uses the unique access key ID
  * to remove the key.
  * @author Ashish Sharma
  */
  deleteAiChatbotAccessKey: async (userAiAccessKeyId) => {
    const url = `${APIConstants.ENDPOINT_AI_CHATBOT_ACCESS_KEYS}?userAiAccessKeyId=${userAiAccessKeyId}`;

    if (getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN)) {
      setAuthToken(getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN));
    }

    try {
      const response = await APIService.delete(url);
      return response?.data;
    } catch (err) {
      DebugService.error("AIChatbotService: Error deleting AI access key", err);
      throw err;
    }
  },

  /**
  * =========================
  * AI CHATBOT APIS
  * =========================
  */

  /**
  * @description Sends a question to the AI chatbot along with contextual JSON data for analysis. 
  * The AI Chatbot processes the provided data and returns relevant answers based on
  * the question and source context.
  * Used for AI-powered insights across the application.
  * @author Ashish Sharma
  */
  askAiChatbot: async (payload) => {
    const url = APIConstants.ENDPOINT_AI_CHATBOT_ASK;

    if (getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN)) {
      setAuthToken(getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN));
    }

    try {
      const response = await APIService.post(url, payload);
      return response?.data || [];
    } catch (err) {
      DebugService.error("AIChatbotService: Error asking AI question", err);
      throw err;
    }
  },

  /**
  * @description Fetches the last five AI chatbot conversations
  * for the logged-in user. Optionally filters chat history
  * based on the source from which the AI interaction was initiated.
  * @author Ashish Sharma
  */
  getAiChatbotHistory: async (sourceFrom) => {
    let url = APIConstants.ENDPOINT_AI_CHATBOT_CHAT_HISTORY;

    if (sourceFrom) {
      url = `${url}?sourceFrom=${encodeURIComponent(sourceFrom)}`;
    }

    if (getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN)) {
      setAuthToken(getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN));
    }

    try {
      const response = await APIService.get(url);
      return response?.data || [];
    } catch (err) {
      DebugService.error("AIChatbotService: Error fetching chat history", err);
      throw err;
    }
  },
};

export default AIChatbotService;